/* eslint-disable react-hooks/exhaustive-deps */
import {
  Button as MaterialButton,
  Grid,
  IconButton,
  makeStyles,
  Tooltip,
  Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import RefreshButton from 'components/RefreshButton';
import TableItems, {
  MenuActionItem,
  MenuActions,
  TableCell,
  TableRow,
} from 'components/TableItems';
import Permission from 'components/ValidPermission';
import i18n from 'i18n';
import { getAllEnvironmentsRequest } from 'modules/environments/actions';
import {
  EnvironmentFields,
  ListEnvironmentDataProps,
} from 'modules/environments/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import InputIcon from '@material-ui/icons/Input';
import { Link, useHistory } from 'react-router-dom';
import ApplicationState from 'store/interfaces';
import { i18nReload } from 'utils/helpers';
import WarningOutlinedIcon from '@material-ui/icons/WarningOutlined';
import clsx from 'clsx';
import { formatDateValue } from 'utils/dateFormat';
import {
  CompanyData,
  PartnerData,
  RolesEnum,
  PermissionsData,
} from 'modules/authenticate/interfaces';
import {
  inAdminView,
  inOperatorView,
  inSuperAdminView,
  isClientOperator,
} from 'utils/role';
import { useTableFilters } from 'hooks/useTableFilters';
import { SearchBar } from 'components/SearchBar';
import EnvironmentsTemplate from '../Templates/Environments';
import styles from './styles';
import { requiredPerformancePermissions } from '../Performance';

const useStyles = makeStyles(styles);

export const dictionary = {
  behavior: {
    manual: 'Manual',
    period: 'PERIODS_KEY',
    micro: 'Micro',
    spot_period: 'PERIODS_KEY',
    haas: 'SAP HAAS',
    ccc: 'SAP CCC',
  },
  virtualizer: {
    connect: 'GoGlobal',
    connect_direct: 'GoGlobal Direct',
    rdp: `Microsoft Remote Desktop (${i18n.t('FILE_KEY')})`,
    rdp_direct: 'Microsoft Remote Desktop (Direct Connect)',
    rdp_app: 'Microsoft Remote Desktop (RemoteApp)',
    tsplus: 'Web Browser',
    tsplus_remote_app: 'Remote App',
    tsplus_all: 'Web e Remote App',
  },
};

const initialQuery = {
  offset: 0,
  limit: 25,
  search: '',
};

const requiredActionsPermissions: Array<keyof PermissionsData> = [
  'get_environment_maintenance',
  'can_list_env_scheduled_warnings_details',
  'edit_environment',
  'list_all_operators_audit_logs',
  'list_env_session_removal_instances',
  'list_env_auto_block_instances_details',
  'list_environment_cost',
  'list_environment_instances',
  'list_models',
  'list_preparation_servers',
  'list_support_servers',
  'publish_image',
  'publish_image_v2',
];

interface Props {
  isLoading: boolean;
  environmentList: ListEnvironmentDataProps;
  partnerSelected: PartnerData | null;
  permissions?: PermissionsData;
  companySelected: CompanyData | null;
  role?: RolesEnum;
  getAllEnvironmentsRequest: (props) => void;
}

const EnvironmentsList = ({
  isLoading,
  environmentList,
  partnerSelected,
  permissions,
  companySelected,
  role,
  getAllEnvironmentsRequest,
}: Props) => {
  const classes = useStyles();

  const {
    itemOrder,
    query,
    searchValue,
    handleOrder,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    setSearchValue,
  } = useTableFilters(initialQuery);

  const history = useHistory();
  const [refresh, setRefresh] = React.useState(false);

  const getEnvironments = (refresh = false) => {
    if (
      (companySelected && partnerSelected) ||
      permissions?.can_view_all_resources
    ) {
      getAllEnvironmentsRequest({
        ...query,
        company_uuid: permissions?.can_view_all_resources
          ? ''
          : companySelected?.id,
        partner_uuid: permissions?.can_view_all_resources
          ? ''
          : partnerSelected?.id,
      });

      setRefresh(refresh);
    }
  };

  React.useEffect(() => {
    getEnvironments();
  }, [getAllEnvironmentsRequest, query, companySelected, partnerSelected]);

  const headers = [
    {
      key: 'name',
      i18n: 'ENVIRONMENT_NAME_KEY',
      styles: { width: 300 },
      order: true,
    },
    {
      key: 'icons',
      i18n: '',
      styles: { width: 15 },
    },
    {
      key: 'environment_uuid',
      i18n: 'UUID',
      styles: { width: 100, padding: 0 },
      hidden: permissions?.can_view_all_resources,
    },
    {
      key: 'partner',
      i18n: 'PARTNER_KEY',
      styles: { width: 300 },
      order: true,
      hidden: !permissions?.can_view_all_resources,
    },
    {
      key: 'behavior',
      i18n: 'MODE_KEY',
      order: true,
      centerTitle: true,
      hidden: isClientOperator(role),
    },
    {
      key: 'virtualizer',
      i18n: 'VIRTUALIZER_KEY',
      order: true,
      centerTitle: true,
    },

    {
      key: 'total_instances',
      i18n: 'INSTANCES_KEY',
      order: true,
      centerTitle: true,
    },
    {
      key: 'license_limit',
      i18n: 'LICENSES_KEY',
      order: true,
      centerTitle: true,
      hidden:
        isClientOperator(role) && !permissions?.can_view_environment_licenses,
    },
    {
      key: 'user_last_access',
      i18n: 'LAST_ACCESS_SINGULAR_KEY',
      order: true,
      centerTitle: true,
    },
    {
      key: '',
      i18n: 'ACTIONS_KEY',
      centerTitle: true,
      hidden:
        !!permissions &&
        requiredActionsPermissions.every(
          permission => !permissions[permission]
        ),
    },
  ].filter(item => !item.hidden);

  const menuActionItems = (item: EnvironmentFields) =>
    [
      {
        key: 'btn-instances',
        label: 'INSTANCES_KEY',
        link: `/environments/${item?.environment_uuid}/instances`,
        hidden: !permissions?.list_environment_instances,
      },
      {
        key: 'btn-servers',
        label: 'SERVERS_KEY',
        link: `/environments/${item?.environment_uuid}/servers`,
        hidden:
          !permissions?.list_preparation_servers &&
          !permissions?.list_support_servers &&
          !permissions?.list_models &&
          !permissions?.publish_image &&
          !permissions?.publish_image_v2,
      },
      {
        key: 'btn-configuration',
        label: 'SETTINGS_KEY',
        link: `/environments/${item?.environment_uuid}/configuration`,
        hidden:
          !permissions?.edit_environment &&
          !permissions?.list_applications_environment,
      },
      {
        key: 'btn-performance',
        label: 'PERFORMANCE_PAGE.PERFORMANCE_KEY',
        link: `/environments/${item?.environment_uuid}/performance`,
        hidden:
          (!!permissions &&
            requiredPerformancePermissions.every(
              permission => !permissions[permission]
            )) ||
          isClientOperator(role),
      },
      {
        key: 'btn-history',
        label: 'HISTORY_SOP_KEY',
        link: `/environments/${item?.environment_uuid}/history`,
        hidden: isClientOperator(role),
      },
      {
        key: 'btn-maintenance',
        label: 'MAINTENANCE_KEY',
        link: `/environments/${item?.environment_uuid}/maintenance`,
        hidden:
          !permissions?.get_environment_maintenance &&
          !permissions?.can_list_env_scheduled_warnings_details &&
          !permissions?.list_env_auto_block_instances_details &&
          !permissions?.list_env_session_removal_instances,
      },
    ].filter(action => !action.hidden);

  const renderIconMaintenance = (item: EnvironmentFields) => {
    if (item?.in_maintenance) {
      return (
        <Tooltip
          placement="bottom"
          arrow
          title={i18n.t('MAINTENANCE_WARNING_KEY')}
        >
          <WarningOutlinedIcon color="error" />
        </Tooltip>
      );
    }
    if (item?.maintenance?.display_warning?.active && !item?.in_maintenance) {
      return (
        <Tooltip
          placement="bottom"
          arrow
          title={i18n.t('MAINTENANCE_DISPLAY_WARNING_KEY')}
        >
          <ErrorOutlineIcon color="primary" />
        </Tooltip>
      );
    }
    return <></>;
  };

  const handleClick = item => {
    if (!!permissions && permissions.list_environment_instances) {
      history.push(`/environments/${item?.environment_uuid}/instances`);
    }
  };

  return (
    <EnvironmentsTemplate
      titlePage=""
      isListContent
      titleContent={i18n.t('ENVIRONMENTS_PAGE.LIST_ENVIRONMENTS_TITLE_KEY')}
      subtitleContent={i18n.t(
        'ENVIRONMENTS_PAGE.LIST_ENVIRONMENTS_SUBTITLE_KEY'
      )}
      actionsContent={
        <Grid
          container
          direction="row"
          alignItems="center"
          justify="flex-end"
          xs={3}
        >
          <RefreshButton
            id="btn-refresh"
            onClick={() => getEnvironments(true)}
            animate={isLoading}
            disabled={isLoading}
          />

          <Permission value="create_new_environment">
            <MaterialButton
              id="new-environment"
              variant="contained"
              size="large"
              className={clsx({
                [classes.addButtonGreen]: inAdminView(role, companySelected),
                [classes.addButtonGray]: inSuperAdminView(
                  role,
                  companySelected
                ),
                [classes.addButtonBlue]: inOperatorView(role, companySelected),
                [classes.addButtonYellow]: companySelected,
              })}
              startIcon={<AddIcon className={classes.addButtonIcon} />}
              component={Link}
              to="/environments/new"
            >
              {i18n.t('ENVIRONMENT_KEY')}
            </MaterialButton>
          </Permission>
        </Grid>
      }
    >
      <>
        <Grid container>
          <SearchBar
            id="search-environments"
            isLoading={isLoading}
            searchValue={searchValue}
            handleSearch={handleSearch}
            setSearchValue={setSearchValue}
          />
          <TableItems
            header={
              <>
                <Permission value="list_environment_instances">
                  <TableCell className={classes.accessButtonHeader} centerTitle>
                    {i18n.t('ACCESS_KEY')}
                  </TableCell>
                </Permission>
                {headers.map(item => (
                  <TableCell
                    centerTitle={item?.centerTitle}
                    style={item?.styles}
                    key={item?.key}
                    order={item?.order}
                    markOderItem={itemOrder === item?.key}
                    handleOrder={() =>
                      item?.order ? handleOrder(item?.key) : null
                    }
                  >
                    {item?.i18n ? (
                      i18n.t(item?.i18n)
                    ) : (
                      <Typography></Typography>
                    )}
                  </TableCell>
                ))}
              </>
            }
            renderItems={(
              item: EnvironmentFields & {
                centerTitle?: boolean;
                styles?: React.CSSProperties;
              },
              index
            ) => (
              <TableRow
                key={item?.environment_uuid}
                className={clsx(classes.summaryRowTable, {
                  [classes.envRow]: !!item?.in_maintenance,
                  [classes.envRowDirect]:
                    !!permissions && permissions.list_environment_instances,
                })}
              >
                <Permission value="list_environment_instances">
                  <TableCell
                    ellipsis
                    centerTitle
                    className={classes.centerFields}
                    onClick={() => {
                      handleClick(item);
                    }}
                  >
                    <IconButton
                      onClick={() => {
                        handleClick(item);
                      }}
                    >
                      <InputIcon className={classes.accessButton} />
                    </IconButton>
                  </TableCell>
                </Permission>
                <TableCell
                  className={classes.nameContent}
                  tooltip={item?.name}
                  copyable
                  maxLength={45}
                >
                  {item?.name}
                </TableCell>
                <TableCell
                  onClick={() => {
                    handleClick(item);
                  }}
                >
                  <Grid item className={classes.icons}>
                    {(item?.in_maintenance || item.maintenance) &&
                      renderIconMaintenance(item)}
                  </Grid>
                </TableCell>
                {permissions?.can_view_all_resources ? (
                  <TableCell tooltip={item?.partner?.name}>
                    <Typography className={classes.partnerName}>
                      {item?.partner?.name}
                    </Typography>
                  </TableCell>
                ) : (
                  <TableCell
                    copyable
                    ellipsis
                    maxLength={25}
                    tooltip={item?.environment_uuid}
                    className={classes.uuidField}
                  >
                    {item?.environment_uuid}
                  </TableCell>
                )}
                {!isClientOperator(role) && (
                  <TableCell
                    onClick={() => {
                      handleClick(item);
                    }}
                    centerTitle
                  >
                    {i18nReload(
                      dictionary.behavior[item?.behavior.type],
                      item?.behavior.type === 'spot_period' ? '(Spot)' : '',
                      i18n.language
                    )}
                  </TableCell>
                )}
                <TableCell
                  onClick={() => {
                    handleClick(item);
                  }}
                  ellipsis
                  maxLength={20}
                  className={classes.virtualizerField}
                >
                  {dictionary.virtualizer[item?.user_connection.virtualizer]}
                </TableCell>
                <TableCell
                  onClick={() => {
                    handleClick(item);
                  }}
                  centerTitle
                >
                  <span
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      paddingRight: 10,
                    }}
                  >
                    {item?.metrics.scale_instances.total_instances}
                  </span>
                </TableCell>
                {(!isClientOperator(role) ||
                  permissions?.can_view_environment_licenses) && (
                  <TableCell
                    onClick={() => {
                      handleClick(item);
                    }}
                    centerTitle
                  >
                    <span
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        paddingRight: 10,
                      }}
                    >
                      {item?.licenses.license_limit}
                    </span>
                  </TableCell>
                )}
                <TableCell
                  onClick={() => {
                    handleClick(item);
                  }}
                  centerTitle
                  textAlignCenter
                  className={classes.dateField}
                >
                  {formatDateValue(item?.user_last_access)}
                </TableCell>
                <Permission value={requiredActionsPermissions}>
                  <TableCell textAlignCenter style={{ width: 10 }}>
                    <MenuActions id={`btn-options-envs-${index}`}>
                      {menuActionItems(item)?.length > 0 ? (
                        menuActionItems(item)?.map(action => (
                          <MenuActionItem
                            key={action?.key}
                            id={action?.key}
                            component={Link}
                            to={action?.link}
                          >
                            <Typography className={classes.menuOptions}>
                              {i18n.t(action?.label)}
                            </Typography>
                          </MenuActionItem>
                        ))
                      ) : (
                        <MenuActionItem>
                          <Typography className={classes.menuOptions}>
                            {i18n.t('NO_ACTIONS_AVAILABLE_KEY')}
                          </Typography>
                        </MenuActionItem>
                      )}
                    </MenuActions>
                  </TableCell>
                </Permission>
              </TableRow>
            )}
            data={environmentList}
            isLoading={!refresh && isLoading}
            onChangePage={handlePageChange}
            onChangeLimit={handleLimitChange}
            notFoundText={i18n.t('ENVIRONMENTS_PAGE.NO_ENVIRONMENT_KEY')}
            alertText={
              (!companySelected || !partnerSelected) &&
              !permissions?.can_view_all_resources
                ? i18n.t('TO_LIST_SELECT_COMPANY_AND_PARTNER_KEY')
                : null
            }
          />
        </Grid>
      </>
    </EnvironmentsTemplate>
  );
};

const mapStateToProps = ({
  authenticate: { companySelected, partnerSelected, data },
  environments: { isLoading, environmentList },
}: ApplicationState) => ({
  isLoading,
  environmentList,
  partnerSelected,
  permissions: data?.permissions,
  companySelected,
  role: data?.role,
});

export default connect(mapStateToProps, {
  getAllEnvironmentsRequest,
})(EnvironmentsList);
